/* eslint-disable tailwindcss/classnames-order */
import { useEffect, useRef, useState } from 'react';
import { ShippingMethod } from '@Types/cart/ShippingMethod';
import Address from 'components/commercetools-ui/adyen-checkout/panels/address';
import Checkout from 'components/commercetools-ui/adyen-checkout/panels/checkout';
import Overview from 'components/commercetools-ui/adyen-checkout/panels/overview';
import OrderSummary from 'components/commercetools-ui/cart/orderSummary';
import { useFormat } from 'helpers/hooks/useFormat';
import { useCart } from 'frontastic';
import { mapToCartStructure, mapToFormStructure } from './mapFormData';
import { requiredDataIsValid } from './requiredDataIsValid';

export type FormData = {
  firstName: string;
  lastName: string;
  phone?: string;
  email: string;
  shippingStreetName: string;
  shippingCity: string;
  shippingPostalCode: string;
  shippingCountry: string;
  billingStreetName: string;
  billingCity: string;
  billingPostalCode: string;
  billingCountry: string;
};

const AdyenCheckout = ({ termsLink, cancellationLink, privacyLink }) => {
  const { data: cartList, updateCart, setShippingMethod } = useCart();
  const { formatMessage } = useFormat({ name: 'cart' });

  const containerRef = useRef();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [disableSubmitButton, setDisableSubmitButton] = useState<boolean>(true);
  const [billingIsSameAsShipping, setBillingIsSameAsShipping] = useState<boolean>(true);
  const [currentShippingMethod, setCurrentShippingMethod] = useState<ShippingMethod>();
  const [dataIsValid, setDataIsValid] = useState<boolean>(false);
  const [data, setData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    shippingStreetName: '',
    shippingCity: '',
    shippingPostalCode: '',
    shippingCountry: '',
    billingStreetName: '',
    billingCity: '',
    billingPostalCode: '',
    billingCountry: '',
  });

  const changeStep = (stepIndex: number) => {
    if (currentStepIndex > stepIndex) {
      setCurrentStepIndex(stepIndex);
    }
  };

  const toggleBillingAddressOption = () => {
    setBillingIsSameAsShipping(!billingIsSameAsShipping);
  };

  const generateStepTag = (index: number) => (
    <div
      className={`mx-auto flex h-10 w-10 items-center rounded-full text-lg  text-white ${
        index == currentStepIndex ? `bg-green-500` : 'border-2 border-gray-200 bg-white'
      }`}
    >
      <span className={`w-full text-center ${index == currentStepIndex ? `text-white` : 'text-gray-600'}`}>
        {index + 1}
      </span>
    </div>
  );

  const goToTopOfPage = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const gotToNextStep = () => {
    if (currentStepIndex == 0) {
      updateCartData();
    }

    setCurrentStepIndex(currentStepIndex + 1);
    goToTopOfPage();
  };

  const updateData = (data: FormData) => {
    setData(data);
  };

  const updateCartData = () => {
    if (dataIsValid) {
      const updatedData = mapToCartStructure(data, billingIsSameAsShipping);

      setIsLoading(true);
      updateCart(updatedData).finally(() => {
        setIsLoading(false);
      });
    }
  };

  const updatecurrentShippingMethod = (shippingMethod: ShippingMethod) => {
    if (shippingMethod?.shippingMethodId) {
      setCurrentShippingMethod(shippingMethod);
      setShippingMethod(shippingMethod.shippingMethodId);
    }
  };

  const submitButtonLabel = [
    formatMessage({ id: 'goToOverview', defaultMessage: 'Go to overview' }),
    formatMessage({ id: 'ContinueAndPay', defaultMessage: 'Continue and pay' }),
  ];

  const steps = [
    {
      name: formatMessage({ id: 'address', defaultMessage: 'Address' }),
      component: (
        <Address
          data={data}
          updateData={updateData}
          billingIsSameAsShipping={billingIsSameAsShipping}
          toggleBillingAddressOption={toggleBillingAddressOption}
        />
      ),
    },
    {
      name: formatMessage({ id: 'overview', defaultMessage: 'Overview' }),
      component: (
        <Overview
          shippingMethods={cartList?.availableShippingMethods}
          currentShippingMethod={currentShippingMethod}
          onSelectShippingMethod={updatecurrentShippingMethod}
        />
      ),
    },
    { name: formatMessage({ id: 'payment', defaultMessage: 'Payment' }), component: <Checkout /> },
  ];

  useEffect(() => {
    setDataIsValid(requiredDataIsValid(data, billingIsSameAsShipping));
  }, [data, billingIsSameAsShipping]);

  useEffect(() => {
    setDisableSubmitButton(!dataIsValid);
  }, [dataIsValid]);

  useEffect(() => {
    if (data.shippingCountry !== '') {
      updateCartData();
    }
  }, [data.shippingCountry, dataIsValid]);

  useEffect(() => {
    const defaultData = mapToFormStructure(cartList);
    if (defaultData && requiredDataIsValid(defaultData, billingIsSameAsShipping)) {
      updateData(defaultData);
    }
  }, [cartList]);

  useEffect(() => {
    if (!currentShippingMethod && cartList?.availableShippingMethods) {
      if (cartList?.shippingInfo) {
        const currentShippingMethod = cartList.availableShippingMethods.find(
          ({ shippingMethodId }) => shippingMethodId == cartList.shippingInfo.shippingMethodId,
        );
        setCurrentShippingMethod(currentShippingMethod);
      } else {
        setCurrentShippingMethod(cartList?.availableShippingMethods?.[0]);
      }
    }
  }, [cartList?.availableShippingMethods]);

  return (
    <div className="mx-auto max-w-4xl md:mt-4">
      <div>
        <div className="mx-auto py-6">
          <div className="relative flex justify-between px-5 py-6 shadow-md md:px-12" id="ProgressStepper">
            <div className="absolute left-0 top-0 flex h-full w-full items-center justify-between px-12 py-6 ">
              <div className="top-2/4 h-2 w-full bg-green-100"></div>
            </div>
            {steps.map(({ name }, index) => (
              <button key={index} className="relative rounded bg-white p-2" onClick={() => changeStep(index)}>
                {generateStepTag(index)}
                <div className="2xl:text-base text-center text-xs">{name}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="lg:grid lg:grid-cols-12 lg:items-start lg:gap-x-12 xl:gap-x-16" ref={containerRef}>
        {steps[currentStepIndex].component}
        <OrderSummary
          cart={cartList}
          submitButtonLabel={submitButtonLabel[currentStepIndex]}
          disableSubmitButton={isLoading || disableSubmitButton}
          showDiscountsForm={currentStepIndex < 2}
          showSubmitButton={currentStepIndex < 2}
          onSubmit={gotToNextStep}
          termsLink={termsLink}
          cancellationLink={cancellationLink}
          privacyLink={privacyLink}
        />
      </div>
    </div>
  );
};

export default AdyenCheckout;
